import React, { useState } from "react";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "./ui/button";

/**
 * Yellow "verify your email" strip shown only for coach accounts whose
 * `email_verified` is false. Sending invites, reminders, creating fees, and
 * creating teams are all backend-gated (returns 403 kind=email_not_verified)
 * so this banner is both a nudge AND the recovery UI.
 *
 * Renders nothing for parents, admins, and verified coaches — no visual noise
 * once verification is done. Refresh AuthContext after a successful resend
 * so a follow-up verify click doesn't need a hard page reload.
 */
export default function VerifyEmailBanner() {
  const { user, refresh } = useAuth();
  const [sending, setSending] = useState(false);

  if (!user || user.role !== "coach" || user.email_verified) return null;

  const onResend = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data.already_verified) {
        toast.success("Your email is already verified", {
          description: "Refresh the page if the banner is still showing.",
        });
        try { await refresh(); } catch { /* noop */ }
        return;
      }
      if (data.email_sent) {
        toast.success("Verification email sent", {
          description: `Check ${user.email} — link expires in 24 hours.`,
        });
      } else {
        toast.warning("Couldn't send the email right now", {
          description: "Please try again in a few minutes.",
        });
      }
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || "Try again in a few minutes.";
      toast.error("Couldn't send verification email", { description: detail });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="mb-4 flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 sm:flex-row sm:items-center sm:justify-between"
      data-testid="verify-email-banner"
      role="status"
    >
      <div className="flex items-start gap-2 text-sm text-amber-900 sm:items-center">
        <Mail className="mt-0.5 h-4 w-4 shrink-0 sm:mt-0" aria-hidden="true" />
        <p className="min-w-0">
          <span className="font-semibold">Verify your email to unlock invites &amp; fees.</span>{" "}
          <span className="text-amber-800">
            We sent a link to <span className="font-semibold">{user.email}</span>.
            Not there? Check spam or resend below.
          </span>
        </p>
      </div>
      <Button
        onClick={onResend}
        disabled={sending}
        variant="outline"
        className="h-9 shrink-0 border-amber-300 bg-white px-3 text-sm font-semibold text-amber-900 hover:bg-amber-100"
        data-testid="verify-email-resend"
      >
        {sending ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Sending…
          </span>
        ) : (
          "Resend verification email"
        )}
      </Button>
    </div>
  );
}
